import { useState, useEffect, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicNav } from "../components/public/PublicNav";
import { PublicFooter } from "../components/public/PublicFooter";
import { SUBSCRIPTION_PLANS, formatContactPlanOption } from "../lib/subscription-plans";
import { apiUrl } from "../api/origin";

type FormState = {
  name: string;
  email: string;
  businessName: string;
  phone: string;
  purpose: string;
  planInterest: string;
  message: string;
};

const EMPTY: FormState = {
  name: "",
  email: "",
  businessName: "",
  phone: "",
  purpose: "",
  planInterest: "",
  message: "",
};

export function Contact() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<FormState>({
    ...EMPTY,
    purpose: searchParams.get("purpose") === "demo" ? "demo" : "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const purpose = searchParams.get("purpose");
    const planInterest = searchParams.get("planInterest");
    setForm((f) => ({
      ...f,
      ...(purpose === "demo" ? { purpose: "demo" } : {}),
      ...(planInterest ? { planInterest } : {}),
    }));
  }, [searchParams]);

  const set = (key: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(apiUrl("/v1/contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({})) as Record<string, unknown>;
      if (!res.ok) {
        setError((data["error"] as string | undefined) ?? "Something went wrong. Please try again.");
        return;
      }
      setSuccess(true);
      setForm(EMPTY);
    } catch {
      setError("Unable to send. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100:ring-brand-500/20 transition";

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden transition-colors duration-200">
      <PublicNav />

      {/* Header */}
      <section className="pt-28 pb-14 relative overflow-hidden bg-gradient-to-b from-brand-50/60 to-white">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-brand-600 text-sm font-semibold tracking-widest uppercase mb-4"
          >
            Get in Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900"
          >
            Let's get your restaurant{" "}
            <span className="gradient-text">on Cravbot</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-4 text-gray-500 max-w-lg mx-auto leading-relaxed"
          >
            {searchParams.get("purpose") === "demo"
              ? "See Cravbot in action. Fill in your details and our team will schedule a live demo tailored to your restaurant."
              : "Cravbot is available to registered restaurants only. Fill in your details and our team will contact you within 24 hours."}
          </motion.p>

          {searchParams.get("purpose") === "demo" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="mt-4 inline-flex items-center gap-2 bg-brand-50 border border-brand-100 rounded-full px-4 py-1.5"
            >
              <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.867V15.133a1 1 0 01-1.447.902L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-brand-700 text-xs font-semibold">Free 30-minute live demo — no commitment</span>
            </motion.div>
          )}
        </div>
      </section>

      {/* Form + info */}
      <section className="pb-28 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Info panel */}
            <div className="lg:col-span-2 space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h2 className="text-gray-900 font-bold text-lg mb-5">Why contact us?</h2>
                <div className="space-y-5">
                  {[
                    {
                      icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      ),
                      iconBg: "bg-brand-50 text-brand-600",
                      title: "Registered Access Only",
                      desc: "Each restaurant is verified before onboarding to ensure quality and security.",
                    },
                    {
                      icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      ),
                      iconBg: "bg-purple-50 text-purple-600",
                      title: "24-Hour Onboarding",
                      desc: "Our team will set up your account, menu, and API credentials within 24 hours.",
                    },
                    {
                      icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      ),
                      iconBg: "bg-green-50 text-green-600",
                      title: "Personalized Setup",
                      desc: "We configure the bot, menu, and business rules to match your restaurant exactly.",
                    },
                  ].map(({ icon, iconBg, title, desc }) => (
                    <div key={title} className="flex gap-3.5">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
                        {icon}
                      </div>
                      <div>
                        <p className="text-gray-700 font-medium text-sm">{title}</p>
                        <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white border border-gray-100 shadow-sm rounded-2xl p-8"
              >
                {success ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-brand-50 flex items-center justify-center mx-auto mb-5">
                      <svg className="w-8 h-8 text-brand-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h2 className="text-gray-900 font-extrabold text-xl mb-2">
                      {form.purpose === "demo" ? "Demo booked!" : "Message sent!"}
                    </h2>
                    <p className="text-gray-500 text-sm max-w-sm mx-auto">
                      {form.purpose === "demo"
                        ? "Thanks! Our team will reach out within 24 hours to schedule your free live demo."
                        : "Thanks for reaching out. Our team will contact you within 24 hours."}
                    </p>
                    <button
                      onClick={() => setSuccess(false)}
                      className="mt-6 text-brand-600 hover:text-brand-500 text-sm font-medium transition-colors"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
                    <div>
                      <h2 className="text-gray-900 font-extrabold text-xl">
                        {form.purpose === "demo" ? "Book a Free Demo" : "Enquiry Form"}
                      </h2>
                      <p className="text-gray-400 text-xs mt-1">All fields are required unless noted.</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-gray-600 text-xs font-semibold uppercase tracking-wide mb-2">
                          Your Name
                        </label>
                        <input type="text" value={form.name} onChange={set("name")} required placeholder="Ahmed Khan" className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-gray-600 text-xs font-semibold uppercase tracking-wide mb-2">
                          Email Address
                        </label>
                        <input type="email" value={form.email} onChange={set("email")} required placeholder="ahmed@restaurant.com" className={inputClass} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-gray-600 text-xs font-semibold uppercase tracking-wide mb-2">
                          Restaurant / Business Name
                        </label>
                        <input type="text" value={form.businessName} onChange={set("businessName")} required placeholder="Yummy Boy Restaurant" className={inputClass} />
                      </div>
                      <div>
                        <label className="block text-gray-600 text-xs font-semibold uppercase tracking-wide mb-2">
                          Phone Number
                        </label>
                        <input type="tel" value={form.phone} onChange={set("phone")} required placeholder="+92 300 1234567" className={inputClass} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-600 text-xs font-semibold uppercase tracking-wide mb-2">
                        How can we help?
                      </label>
                      <select value={form.purpose} onChange={set("purpose")} className={inputClass}>
                        <option value="">I'd like to get started</option>
                        <option value="demo">Book a free demo</option>
                        <option value="question">Technical / general question</option>
                        <option value="pricing">Pricing enquiry</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-600 text-xs font-semibold uppercase tracking-wide mb-2">
                        Plan Interest{" "}
                        <span className="text-gray-300 normal-case font-normal">(optional)</span>
                      </label>
                      <select value={form.planInterest} onChange={set("planInterest")} className={inputClass}>
                        <option value="">Not sure yet</option>
                        {SUBSCRIPTION_PLANS.map((plan) => (
                          <option key={plan.planId} value={plan.planId}>
                            {formatContactPlanOption(plan)}
                          </option>
                        ))}
                        <option value="custom">Other / not sure yet</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-600 text-xs font-semibold uppercase tracking-wide mb-2">
                        Message
                      </label>
                      <textarea
                        value={form.message}
                        onChange={set("message")}
                        required
                        rows={4}
                        placeholder="Tell us about your restaurant, average order volume, or any specific requirements..."
                        className={`${inputClass} resize-none`}
                      />
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-600">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 py-3.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-xl btn-glow transition-all text-sm"
                    >
                      {loading ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          {form.purpose === "demo" ? "Book Demo" : "Send Enquiry"}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
